<template>
  <div
      :class="[
        isExpanded
          ? 'fixed inset-0 z-[9999] flex flex-col bg-white shadow-2xl pt-[env(safe-area-inset-top)] pr-[env(safe-area-inset-right)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)]'
          : compact
            ? 'relative flex h-full min-h-0 flex-col'
            : 'relative mt-4',
      ]">
    <!-- Barra acima do gráfico: mesmo bg, icon buttons -->
    <div v-if="!compact" class="flex shrink-0 items-center justify-end gap-2 border-0 border-b border-gray-200 border-solid bg-white px-2 py-2">
      <button
        type="button"
        class="inline-flex items-center justify-center w-[30px] h-[30px] rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 [&_svg]:text-gray-600 [&_svg]:hover:text-gray-800"
        :class="toolboxOpen ? 'bg-gray-100' : ''"
        title="Caixa de ferramentas"
        @click="toolboxOpen = !toolboxOpen">
        <IcMenu class="h-4 w-4" />
      </button>
      <button class="inline-flex items-center justify-center w-[30px] h-[30px] rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900" title="Expandir área do gráfico" @click="toggleExpanded">
        <IcExpanded v-if="!isExpanded" class="h-4 w-4" />
        <IcClose v-else class="h-4 w-4" />
      </button>
    </div>

    <!-- Painel da toolbox acima do gráfico, mesmo bg -->
    <div v-if="!compact" v-show="toolboxOpen" class="shrink-0 border-0 border-b border-gray-200 border-solid bg-white">
      <ChartToolsPanel
        :lines="referenceLines"
        :mode="activeToolMode"
        :markers-count="markers.length"
        @set-mode="setToolMode"
        @update-horizontal-config="updateHorizontalConfig"
        @remove-line="removeReferenceLine"
        @clear-lines="clearReferenceLines"
        @clear-markers="clearMarkers" />
    </div>

    <!-- Área do gráfico -->
    <div
      :class="[
        'relative flex-1 min-h-0 w-full bg-white',
        isExpanded ? 'mt-0' : compact ? 'h-full' : 'mt-0 h-[450px]',
      ]">
      <div
        ref="chartContainerRef"
        :class="[
          'absolute bg-white',
          isExpanded ? 'inset-4 min-h-0' : compact ? 'inset-0' : 'inset-0 p-2 shadow-md',
        ]" />
      <template v-if="!compact">
        <ChartMarker
          v-for="(marker, index) in markers"
          :key="index"
          :marker="marker"
          compact
          @remove="markers.splice(index, 1)"
          @remove-series="removeSeriesFromMarker(index, $event)"
          @clear-series="clearSeriesOfMarker(index)"
          @drag-start="startMarkerDrag(index, $event)" />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import ChartMarker from '@/components/Globals/Charts/ChartMarker.vue'
import ChartToolsPanel, { ChartToolMode, IChartReferenceLine } from '@/components/Globals/Charts/ChartToolsPanel.vue'
import IcClose from '@/components/Icons/IcClose.vue'
import IcExpanded from '@/components/Icons/IcExpanded.vue'
import IcMenu from '@/components/Icons/IcMenu.vue'
import { convertToLocalTimeUnix } from '@/composable/useFormatDateAndHours'
import { ILineChartMarker } from '@/interfaces/components/chats/chart-marker'
import { CyclePeriod } from '@/services/cycle-analysis.service'
import { useRegisterStore } from '@/store/register'
import { useRegisterChartPresentation } from '@/composables/useRegisterChartPresentation'
import { DOOR_CORRELATION_OPEN_COLOR } from '@/utils/dashboard/door-correlation-background'
import { buildChartOption, type IRegisterChartSensorData } from '@/utils/components/register-chart'
import type { EChartsOption } from 'echarts'
import * as echarts from 'echarts'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, type PropType } from 'vue'

const props = defineProps({
  sensorData: Object,
  secondarySensorData: {
    type: Object as PropType<IRegisterChartSensorData | null>,
    default: null,
  },
  correlationSensors: {
    type: Array as PropType<IRegisterChartSensorData[]>,
    default: () => [],
  },
  cyclePeriods: {
    type: Array as () => CyclePeriod[],
    default: () => [],
  },
  sensorIndex: {
    type: Number,
    default: null,
  },
  compact: {
    type: Boolean,
    default: false,
  },
})

const chartContainerRef = ref<HTMLDivElement | null>(null)
let chartInstance: echarts.ECharts | null = null
const DEBUG_CHART = false

function debugLog(..._args: unknown[]) {
  if (!DEBUG_CHART) return
}

const {
  chartSeries,
  xAxisMin,
  xAxisMax,
  primaryUnitDisplay,
  secondaryUnitDisplay,
  unitDisplay,
  doorOpenLegendLabel,
  yAxisOption,
  hasDualAxis,
  isMultiGrid,
  multiGridConfig,
  doorCorrelationBackground,
} = useRegisterChartPresentation(
  () => props.sensorData as IRegisterChartSensorData | undefined,
  () => props.secondarySensorData,
  () => props.correlationSensors,
)

const markers = ref<ILineChartMarker[]>([])
const referenceLines = ref<IChartReferenceLine[]>([])
const activeToolMode = ref<ChartToolMode>('vertical')
const horizontalDraft = ref<{ color: string; label: string }>({ color: '#ef4444', label: '' })
const isExpanded = ref(false)
const toolboxOpen = ref(false)
const draggingMarkerIndex = ref<number | null>(null)
const dragOffset = ref<{ x: number; y: number }>({ x: 0, y: 0 })

const registerStore = useRegisterStore()

const analysisAnnotations = computed(() => {
  if (props.sensorIndex === null) return []
  return registerStore.getAnalysisAnnotations(props.sensorIndex)
})

/** Duração do período em horas — guia formato e densidade dos rótulos do eixo X. */
const periodDurationHours = computed(() => {
  const min = xAxisMin.value
  const max = xAxisMax.value
  if (min == null || max == null) return 24
  return (max - min) / (60 * 60 * 1000)
})

/** Quando os pontos estão a ≤60s de intervalo, exibir segundos no eixo e no tooltip. */
const showSecondsInTime = computed(() => {
  const raw = chartSeries.value
  if (!Array.isArray(raw) || raw.length === 0) return false
  const firstSeriesData = raw[0]?.data ?? []
  if (firstSeriesData.length < 2) return false
  const intervals: number[] = []
  for (let i = 1; i < firstSeriesData.length; i++) {
    const a = firstSeriesData[i - 1].x instanceof Date ? firstSeriesData[i - 1].x.getTime() : new Date(firstSeriesData[i - 1].x).getTime()
    const b = firstSeriesData[i].x instanceof Date ? firstSeriesData[i].x.getTime() : new Date(firstSeriesData[i].x).getTime()
    intervals.push((b - a) / 1000)
  }
  if (intervals.length === 0) return false
  intervals.sort((x, y) => x - y)
  const medianSeconds = intervals[Math.floor(intervals.length / 2)] ?? 0
  return medianSeconds <= 60
})

const xAxisSplitNumber = computed(() => {
  if (props.compact) {
    if (periodDurationHours.value <= 6) return 3
    if (periodDurationHours.value <= 24) return 4
    return 5
  }
  return showSecondsInTime.value ? 8 : 12
})

const timeFormatOptions = computed(() =>
  showSecondsInTime.value
    ? { day: '2-digit' as const, month: '2-digit' as const, year: 'numeric' as const, hour: '2-digit' as const, minute: '2-digit' as const, second: '2-digit' as const }
    : { day: '2-digit' as const, month: '2-digit' as const, year: 'numeric' as const, hour: '2-digit' as const, minute: '2-digit' as const },
)

/** Formato mais curto só para o eixo X (evita poluição visual). Tooltip continua com formato completo. */
const xAxisTimeFormat = (value: number, withSeconds: boolean): string => {
  const d = new Date(value)
  const hours = periodDurationHours.value

  if (props.compact) {
    if (hours <= 24) {
      return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    }
    if (hours <= 24 * 7) {
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
    }
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })
  }

  if (withSeconds) {
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  }
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

// Marks compartilhados (análise + linhas de referência + marcadores) para os modos de gráfico.
const analysisMarkAreaData = computed(() => {
  if (analysisAnnotations.value.length === 0) return undefined
  return analysisAnnotations.value.map((ann, idx) => [
    {
      xAxis: convertToLocalTimeUnix(ann.start),
      itemStyle: { color: (ann as { color?: string }).color || 'rgba(202, 138, 4, 0.2)' },
      label: {
        show: true,
        position: 'insideTop',
        color: '#fff',
        fontSize: 10,
        formatter: () => (ann as { label?: string }).label || `Ciclo ${idx + 1}`,
      },
    },
    { xAxis: convertToLocalTimeUnix(ann.end) },
  ])
})

const horizontalReferenceLineData = computed(() =>
  referenceLines.value.length > 0
    ? referenceLines.value.map((line) => ({
        name: line.label,
        yAxis: line.value,
        lineStyle: {
          color: line.color || '#ef4444',
          width: 2,
          type: 'dashed' as const,
        },
        label: {
          show: true,
          position: 'insideMiddleTop' as const,
          formatter: line.label?.trim() ? `${line.label}` : `${line.value}`,
          color: line.color || '#ef4444',
        },
      }))
    : undefined,
)

const verticalMarkerLineData = computed(() =>
  markers.value.length > 0
    ? markers.value.map((marker) => ({
        xAxis: marker.x.getTime(),
        lineStyle: {
          color: '#2563eb',
          width: 1,
          type: 'dotted' as const,
        },
        label: {
          show: false,
        },
      }))
    : undefined,
)

const allMarkLineData = computed(() => [
  ...(horizontalReferenceLineData.value || []),
  ...(verticalMarkerLineData.value || []),
])

const isDoorCorrelation = computed(
  () => !isMultiGrid.value && doorCorrelationBackground.value != null,
)

// Faixas de porta aberta no gráfico principal (correlação com UMA porta).
const doorBandSeries = computed((): echarts.SeriesOption[] =>
  isDoorCorrelation.value && doorCorrelationBackground.value
    ? [
        {
          name: doorOpenLegendLabel.value,
          type: 'line',
          data: [],
          showSymbol: false,
          lineStyle: { opacity: 0, width: 0 },
          itemStyle: { color: DOOR_CORRELATION_OPEN_COLOR },
          tooltip: { show: false },
          silent: true,
          z: 0,
          markArea: {
            silent: true,
            z: 0,
            data: doorCorrelationBackground.value.markAreaData as any,
          },
        },
      ]
    : [],
)

const legendData = computed(() => {
  const names = echartsSeries.value.map((s) => String(s.name ?? '')).filter(Boolean)
  if (!isDoorCorrelation.value) return names
  const rest = names.filter((n) => n !== doorOpenLegendLabel.value)
  return [
    {
      name: doorOpenLegendLabel.value,
      icon: 'rect',
      itemStyle: { color: DOOR_CORRELATION_OPEN_COLOR },
    },
    ...rest,
  ]
})

// Séries no formato ECharts: { name, data: [[timestamp, value], ...] }
const echartsSeries = computed((): echarts.SeriesOption[] => {
  const raw = chartSeries.value

  if (!Array.isArray(raw) || raw.length === 0) {
    return doorBandSeries.value
  }

  const lineSeries = raw.map((s) => {
    const data = (s.data || []).map((point) => {
      const t = point.x instanceof Date ? point.x.getTime() : new Date(point.x).getTime()
      return [t, point.y] as [number, number]
    })

    return {
      name: s.name,
      type: 'line' as const,
      smooth: false,
      symbol: 'none',
      lineStyle: { width: 2 },
      yAxisIndex: s.yAxisIndex ?? 0,
      z: 2,
      data,
      markArea: {
        silent: true,
        z: 0,
        data: (analysisMarkAreaData.value ?? []) as any,
      },
      markLine: allMarkLineData.value.length ? { symbol: 'none', data: allMarkLineData.value } : undefined,
    }
  })

  return [...doorBandSeries.value, ...lineSeries]
})

const buildMultiGridOption = (): EChartsOption => {
  const config = multiGridConfig.value
  if (!config) return {}

  return buildChartOption({
    ...config,
    doorOpenLegendLabel: doorOpenLegendLabel.value,
    splitNumber: xAxisSplitNumber.value,
    compact: props.compact,
    expanded: isExpanded.value,
    xAxisLabelFormatter: (value: number) => xAxisTimeFormat(value, showSecondsInTime.value),
    topGridMark: {
      markLineData: allMarkLineData.value.length ? allMarkLineData.value : undefined,
      markAreaData: analysisMarkAreaData.value,
    },
  })
}

const buildOption = (): EChartsOption => ({
  tooltip: {
    trigger: 'axis',
    axisPointer: { type: 'cross' },
    formatter: (params: unknown) => {
      const arr = Array.isArray(params) ? params : [params]
      const seriesByName = new Map(chartSeries.value.map((s) => [s.name, s]))
      const lines = (
        arr as Array<{ seriesName?: string; value?: [number, number]; seriesIndex?: number }>
      )
        .filter((p) => p?.value?.[1] != null)
        .map((p) => {
          const seriesMeta = p.seriesName ? seriesByName.get(p.seriesName) : undefined
          const unit =
            seriesMeta?.unitDisplay ??
            (seriesMeta?.yAxisIndex === 1 ? secondaryUnitDisplay.value : primaryUnitDisplay.value)
          return `${p.seriesName}: <strong>${Number(p.value![1]).toFixed(2)} ${unit}</strong>`
        })
      if (lines.length === 0) return ''
      const date = Array.isArray(params) && (params as any)[0] ? new Date((params as any)[0].value[0]) : new Date()
      const timeStr = date.toLocaleString('pt-BR', timeFormatOptions.value)
      return `${timeStr}<br/>${lines.join('<br/>')}`
    },
  },
  legend: {
    data: legendData.value,
    top: 0,
    textStyle: { fontSize: props.compact ? 10 : 12 },
  },
  grid: {
    left: '2%',
    right: hasDualAxis.value ? '3%' : '2%',
    bottom: isExpanded.value ? '11%' : props.compact ? 28 : 28,
    top: props.compact ? '14%' : '12%',
    containLabel: true,
  },
  dataZoom: [
    {
      type: 'inside',
      xAxisIndex: 0,
      zoomOnMouseWheel: true,
      moveOnMouseMove: true,
      preventDefaultMouseMove: true,
    },
    {
      type: 'slider',
      xAxisIndex: 0,
      height: isExpanded.value ? 28 : props.compact ? 14 : 16,
      bottom: isExpanded.value ? '2%' : props.compact ? 2 : 4,
      borderColor: 'transparent',
      fillerColor: 'rgba(99, 102, 241, 0.2)',
      handleStyle: { color: '#6366f1' },
      dataBackground: { lineStyle: { opacity: 0.3 }, areaStyle: { opacity: 0.05 } },
      moveHandleSize: isExpanded.value ? 10 : 6,
      textStyle: { fontSize: isExpanded.value ? 11 : props.compact ? 9 : 10 },
      ...(props.compact
        ? {
            labelFormatter: (value: number) => xAxisTimeFormat(value, showSecondsInTime.value),
          }
        : {}),
    },
  ],
  xAxis: {
    type: 'time',
    min: xAxisMin.value,
    max: xAxisMax.value,
    boundaryGap: [0, 0],
    splitNumber: xAxisSplitNumber.value,
    axisLabel: {
      fontSize: props.compact ? 9 : 10,
      hideOverlap: true,
      showMinLabel: true,
      showMaxLabel: true,
      margin: props.compact ? 10 : 8,
      formatter: (value: number) => xAxisTimeFormat(value, showSecondsInTime.value),
    },
  },
  yAxis: yAxisOption.value,
  series: echartsSeries.value,
})

const buildCurrentOption = (): EChartsOption =>
  isMultiGrid.value ? buildMultiGridOption() : buildOption()

function initChart() {
  if (!chartContainerRef.value) {
    debugLog('initChart abortado: chartContainerRef ausente')
    return
  }
  if (chartInstance) {
    chartInstance.dispose()
    chartInstance = null
  }
  chartInstance = echarts.init(chartContainerRef.value)
  debugLog('initChart OK', {
    sensorIndex: props.sensorIndex,
    seriesCount: echartsSeries.value.length,
    xAxisMin: xAxisMin.value,
    xAxisMax: xAxisMax.value,
  })
  previousViewKey = null
  chartInstance.setOption(buildCurrentOption())
  previousViewKey = viewKey()

  const zr = chartInstance.getZr()
  zr.on('click', (ev: any) => {
    if (!chartInstance || props.compact) return
    const clickX = Number(ev?.offsetX ?? 0)
    const clickY = Number(ev?.offsetY ?? 0)

    const dataCoord = chartInstance.convertFromPixel({ xAxisIndex: 0, yAxisIndex: 0 }, [clickX, clickY]) as [number, number]
    if (!Array.isArray(dataCoord) || dataCoord.length < 2) {
      debugLog('click descartado: convertFromPixel inválido', { clickX, clickY, dataCoord })
      return
    }

    const clickedTimestamp = Number(dataCoord[0])
    const clickedY = Number(dataCoord[1])
    if (Number.isNaN(clickedTimestamp)) {
      debugLog('click descartado: timestamp inválido', dataCoord)
      return
    }

    if (activeToolMode.value === 'horizontal') {
      if (Number.isNaN(clickedY)) {
        debugLog('click horizontal descartado: y inválido', dataCoord)
        return
      }
      referenceLines.value.push({
        value: clickedY,
        color: horizontalDraft.value.color || '#ef4444',
        label: horizontalDraft.value.label?.trim() || `Linha ${referenceLines.value.length + 1}`,
      })
      debugLog('linha horizontal criada', referenceLines.value[referenceLines.value.length - 1])
      return
    }

    if (activeToolMode.value !== 'vertical') {
      debugLog('click ignorado: modo desativado')
      return
    }

    // Restringe ao grid superior (contínuo) — o grid inferior (portas) não cria marcadores.
    if (!chartInstance.containPixel({ gridIndex: 0 }, [clickX, clickY])) {
      debugLog('click descartado: clicou fora da grade superior')
      return
    }

    let nearest: { seriesName: string; timestamp: number; value: number } | null = null
    let minDistance = Number.POSITIVE_INFINITY

    for (const s of echartsSeries.value as Array<any>) {
      const points = Array.isArray(s?.data) ? s.data : []
      for (const p of points) {
        if (!Array.isArray(p) || p.length < 2) continue
        const ts = Number(p[0])
        const val = Number(p[1])
        if (Number.isNaN(ts) || Number.isNaN(val)) continue
        const dist = Math.abs(ts - clickedTimestamp)
        if (dist < minDistance) {
          minDistance = dist
          nearest = { seriesName: String(s?.name ?? 'Série'), timestamp: ts, value: val }
        }
      }
    }

    if (!nearest) {
      debugLog('click descartado: sem ponto próximo')
      return
    }

    const nearestSeriesMeta = chartSeries.value.find(
      (cs) => String(cs.name ?? '') === String(nearest?.seriesName ?? ''),
    )
    const nearestUnit = nearestSeriesMeta?.unitDisplay ?? unitDisplay.value

    const valuesAtTimestamp: Array<{ seriesName: string; value: string }> = []
    for (const s of echartsSeries.value as Array<any>) {
      const points = Array.isArray(s?.data) ? s.data : []
      let bestPoint: [number, number] | null = null
      let bestDist = Number.POSITIVE_INFINITY
      for (const p of points) {
        if (!Array.isArray(p) || p.length < 2) continue
        const ts = Number(p[0])
        const val = Number(p[1])
        if (Number.isNaN(ts) || Number.isNaN(val)) continue
        const dist = Math.abs(ts - nearest.timestamp)
        if (dist < bestDist) {
          bestDist = dist
          bestPoint = [ts, val]
        }
      }
      if (bestPoint) {
        const seriesName = String(s?.name ?? 'Série')
        const seriesMeta = chartSeries.value.find((cs) => String(cs.name ?? '') === seriesName)
        valuesAtTimestamp.push({
          seriesName,
          value: `${Number(bestPoint[1]).toFixed(2)} ${seriesMeta?.unitDisplay ?? unitDisplay.value}`,
        })
      }
    }

    const positionX = clickX
    const positionY = clickY

    debugLog('marker por clique', {
      clickPixel: [clickX, clickY],
      clickedTimestamp,
      nearest,
      finalPosition: [positionX, positionY],
    })

    markers.value.push({
      seriesName: nearest.seriesName,
      x: new Date(nearest.timestamp),
      y: `${nearest.value.toFixed(2)} ${nearestUnit}`,
      positionX,
      positionY,
      values: [...valuesAtTimestamp],
      allValues: [...valuesAtTimestamp],
    })
    debugLog('marker criado', { totalMarkers: markers.value.length })
  })

  chartInstance.on('dataZoom', () => {
    debugLog('dataZoom detectado: limpando markers')
    if (markers.value.length === 0) return
    markers.value = []
  })
}

/** Troca de modo visual (single ↔ dual-axis ↔ multi-grid) exige rebuild completo. */
let previousViewKey: string | null = null

function viewKey(): string {
  return `${isMultiGrid.value ? 'multi-grid' : hasDualAxis.value ? 'dual-axis' : 'single'}|expanded:${isExpanded.value}`
}

function updateChart() {
  if (!chartInstance) return
  debugLog('updateChart', {
    seriesCount: echartsSeries.value.length,
    markersCount: markers.value.length,
    referenceLinesCount: referenceLines.value.length,
  })

  const key = viewKey()
  const isModeChange = previousViewKey !== null && previousViewKey !== key
  previousViewKey = key

  if (isModeChange) {
    // Rebuild completo: remove grids/eixos/dataZoom órfãos sem depender de merge.
    chartInstance.setOption(buildCurrentOption(), { notMerge: true })
    return
  }

  // replaceMerge estrutural evita que components do modo timeline (grid/xAxis/yAxis
  // adicionais, eixos de nomes de porta) fiquem órfãos.
  // ATENÇÃO (1): NÃO incluir 'tooltip' — replaceMerge de tooltip durante interação de
  // zoom recicla o elemento HTML do tooltip e causa NPE ("el is null" em innerHTML,
  // apache/echarts#19827).
  // ATENÇÃO (2): NÃO incluir 'dataZoom' nos updates normais — replaceMerge recriaria o
  // dataZoom (start 0/end 100) a cada atualização, desfazendo o zoom aplicado.
  chartInstance.setOption(buildCurrentOption(), {
    replaceMerge: ['series', 'legend', 'grid', 'xAxis', 'yAxis', 'axisPointer'],
  })
}

function clearMarkers() {
  markers.value = []
}

function removeSeriesFromMarker(markerIndex: number, seriesName: string) {
  const marker = markers.value[markerIndex]
  if (!marker?.values) return
  marker.values = marker.values.filter((v) => v.seriesName !== seriesName)
}

function clearSeriesOfMarker(markerIndex: number) {
  const marker = markers.value[markerIndex]
  if (!marker) return
  marker.values = []
}

function setToolMode(mode: ChartToolMode) {
  activeToolMode.value = mode
}

function updateHorizontalConfig(config: { color: string; label: string }) {
  horizontalDraft.value = { ...config }
}

function toggleExpanded() {
  isExpanded.value = !isExpanded.value
  nextTick(() => chartInstance?.resize())
}

function startMarkerDrag(index: number, event: MouseEvent) {
  if (!chartContainerRef.value) return
  const marker = markers.value[index]
  if (!marker) return
  const rect = chartContainerRef.value.getBoundingClientRect()
  const pointerX = event.clientX - rect.left
  const pointerY = event.clientY - rect.top
  dragOffset.value = {
    x: pointerX - marker.positionX,
    y: pointerY - marker.positionY,
  }
  draggingMarkerIndex.value = index
}

function onWindowMouseMove(event: MouseEvent) {
  if (draggingMarkerIndex.value === null || !chartContainerRef.value) return
  const marker = markers.value[draggingMarkerIndex.value]
  if (!marker) return
  const rect = chartContainerRef.value.getBoundingClientRect()
  const x = event.clientX - rect.left - dragOffset.value.x
  const y = event.clientY - rect.top - dragOffset.value.y
  marker.positionX = x
  marker.positionY = y
}

function stopMarkerDrag() {
  draggingMarkerIndex.value = null
}

function removeReferenceLine(index: number) {
  referenceLines.value.splice(index, 1)
}

function clearReferenceLines() {
  referenceLines.value = []
}

const handleResize = () => chartInstance?.resize()

watch(
  [echartsSeries, analysisAnnotations, unitDisplay, xAxisMin, xAxisMax, referenceLines, markers, isMultiGrid, multiGridConfig],
  () => {
    nextTick(() => updateChart())
  },
  { deep: true },
)

watch(isExpanded, () => {
  nextTick(() => chartInstance?.resize())
})

let resizeObserver: ResizeObserver | null = null
let resizeTimeout: ReturnType<typeof setTimeout> | null = null

onMounted(() => {
  nextTick(() => {
    initChart()
    if (chartContainerRef.value) {
      resizeObserver = new ResizeObserver(() => {
        if (resizeTimeout) clearTimeout(resizeTimeout)
        resizeTimeout = setTimeout(() => {
          chartInstance?.resize()
        }, 25)
      })
      resizeObserver.observe(chartContainerRef.value)
    }
  })
  window.addEventListener('resize', handleResize)
  window.addEventListener('mousemove', onWindowMouseMove)
  window.addEventListener('mouseup', stopMarkerDrag)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('mousemove', onWindowMouseMove)
  window.removeEventListener('mouseup', stopMarkerDrag)
  if (resizeTimeout) clearTimeout(resizeTimeout)
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
  if (chartInstance) {
    chartInstance.dispose()
    chartInstance = null
  }
})
</script>
